'use client';

import { useFormatter, useLocale, useNow, useTranslations } from 'next-intl';
import { ChevronDown } from 'lucide-react';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import { listingCanonicalUrl } from '@/src/lib/seo/canonical';
import { serializeJsonLd } from '@/src/lib/seo/json-ld';
import { buildFaqJsonLd, type FaqQuestion, getHdpFaqQuestions } from './faqController';
import { answerBody, answerList, answerListHeader, caret, container, faqItem, faqSummary } from './faqModule.styles';

type Props = {
  listing: PropertyDetailListing;
  /** Canonical page URL, when the caller knows it better than uriMalone (the BDP). */
  pageUrl?: string;
};

const renderAnswer = (question: FaqQuestion) => {
  const type = question.answerType ?? 'answer';

  if (type === 'answer') {
    return <p className={answerBody}>{question.answer as string}</p>;
  }

  const items = Array.isArray(question.answer) ? question.answer : [];
  const ListTag = type === 'unorderedList' ? 'ul' : 'ol';

  return (
    <>
      {question.listHeader && <p className={answerListHeader}>{question.listHeader}</p>}
      <ListTag className={answerList}>
        {items.map((item, idx) => (
          <li key={`faq-list-item-${idx}`}>{item}</li>
        ))}
      </ListTag>
    </>
  );
};

export const FaqModule = ({ listing, pageUrl }: Props) => {
  const t = useTranslations('hdp');
  const locale = useLocale();
  const format = useFormatter();
  const now = useNow();
  const updatedDate = (listing as { history?: { lastUpdated?: { date?: number } } }).history?.lastUpdated?.date;
  const date = typeof updatedDate === 'number' && updatedDate > 0 ? new Date(updatedDate) : undefined;
  const updatedAgo =
    locale !== 'en' && date && Number.isFinite(date.getTime()) ? format.relativeTime(date, now) : undefined;
  const questions = getHdpFaqQuestions(listing, t, { locale, updatedAgo });

  if (questions.length === 0) {
    return null;
  }

  // The client module registry only renders this on the pad page, where
  // uriMalone is the canonical path. The BDP renders it directly and passes
  // its own building URL.
  const faqPageUrl = pageUrl ?? (listing.uriMalone ? listingCanonicalUrl(listing.uriMalone) : undefined);
  const faqJsonLd = buildFaqJsonLd(questions, faqPageUrl);

  return (
    <>
      {faqJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(faqJsonLd) }} />
      )}
      <div className={container}>
        {questions.map((q) => (
          <details key={`faq-${q.question}`} className={faqItem}>
            <summary className={faqSummary}>
              <span>{q.question}</span>
              <LucideIcon icon={ChevronDown} size="sm" className={caret} />
            </summary>
            {renderAnswer(q)}
          </details>
        ))}
      </div>
    </>
  );
};
